import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArchiveIcon from "@mui/icons-material/Archive";
import axios from "api/axios";
import PageShell, { SectionCard } from "components/patch/PageShell";
import SimpleTable from "components/patch/SimpleTable";

export default function Payslips() {
  const [runs, setRuns] = useState([]);
  const [archives, setArchives] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);

  const selectedRun = useMemo(
    () => runs.find((run) => String(run.id) === String(selectedRunId)),
    [runs, selectedRunId]
  );

  const loadData = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [runsRes, archivesRes] = await Promise.all([
        axios.get("/payslip-archives/runs"),
        axios.get("/payslip-archives"),
      ]);

      const runRows = runsRes.data?.data || [];
      const archiveRows = archivesRes.data?.data?.data || archivesRes.data?.data || [];
      setRuns(runRows);
      setArchives(Array.isArray(archiveRows) ? archiveRows : []);

      if (!selectedRunId && runRows.length > 0) {
        setSelectedRunId(String(runRows[0].id));
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.message || "Unable to load payslip archive data.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const generateArchive = async (regenerate = false) => {
    if (!selectedRunId) {
      setMessage({ type: "warning", text: "Please select a payroll run first." });
      return;
    }

    setGenerating(true);
    setMessage(null);
    try {
      const res = await axios.post("/payslip-archives/generate", {
        payroll_run_id: selectedRunId,
        regenerate,
      });
      setMessage({ type: "success", text: res.data?.message || "Archive generated." });
      await loadData();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.message || "Unable to generate payslip archive.",
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadArchive = async (archive) => {
    try {
      const response = await axios.get(`/payslip-archives/${archive.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", archive.zip_path?.split("/").pop() || "payslips.zip");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.response?.data?.message || "Unable to download archive.",
      });
    }
  };

  const columns = [
    { label: "Month", key: "month" },
    { label: "Run ID", render: (row) => row.payroll_run_id },
    {
      label: "Status",
      render: (row) => (
        <Chip
          size="small"
          label={row.status}
          color={
            row.status === "completed"
              ? "success"
              : row.status === "processing"
              ? "warning"
              : "default"
          }
        />
      ),
    },
    { label: "Total Staff", key: "total_staff" },
    { label: "Generated", key: "generated_count" },
    { label: "Failed", key: "failed_count" },
    { label: "Generated At", render: (row) => row.generated_at || "-" },
    {
      label: "Action",
      render: (row) => (
        <Button
          size="small"
          variant="contained"
          startIcon={<DownloadIcon />}
          disabled={!row.zip_path}
          onClick={() => downloadArchive(row)}
        >
          Download ZIP
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title="Bulk Payslip Archive"
      subtitle="Generate individual staff payslips as PDFs, save them by payroll month using PF number filenames, and download all payslips as one ZIP archive."
      actions={
        <Button
          startIcon={<RefreshIcon />}
          variant="outlined"
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      }
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={5}>
          <SectionCard
            title="Generate Archive"
            subtitle="Select a payroll run that already has payroll items."
          >
            <Stack spacing={2}>
              {message ? <Alert severity={message.type}>{message.text}</Alert> : null}

              <TextField
                select
                fullWidth
                label="Payroll Run"
                value={selectedRunId}
                onChange={(event) => setSelectedRunId(event.target.value)}
                disabled={loading || generating}
              >
                {runs.map((run) => (
                  <MenuItem key={run.id} value={String(run.id)}>
                    Run #{run.id} - {run.month} - {run.status} ({run.items_count || 0} staff)
                  </MenuItem>
                ))}
              </TextField>

              {selectedRun ? (
                <Box
                  sx={{ p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e5e7eb" }}
                >
                  <Typography variant="body2">
                    <strong>Month:</strong> {selectedRun.month}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedRun.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Payroll Items:</strong> {selectedRun.items_count || 0}
                  </Typography>
                </Box>
              ) : null}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={16} /> : <ArchiveIcon />}
                  onClick={() => generateArchive(false)}
                  disabled={generating || !selectedRunId}
                >
                  Generate ZIP
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => generateArchive(true)}
                  disabled={generating || !selectedRunId}
                >
                  Regenerate
                </Button>
              </Stack>

              <Alert severity="info">
                Files are saved as{" "}
                <strong>storage/app/public/payslips/YYYY-MM/run-ID/PF_NO.pdf</strong>. Run{" "}
                <strong>php artisan storage:link</strong> once if downloads are not accessible.
              </Alert>
            </Stack>
          </SectionCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <SectionCard
            title="Archive History"
            subtitle="Download previously generated payslip ZIP files."
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <SimpleTable columns={columns} rows={archives} />
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </PageShell>
  );
}
